import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { Pencil, Trash2, Check, X, Reply } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import MentionTextarea from './MentionTextarea';
import { renderCommentBody } from './commentUtils';
import { commentsService } from '../../services/comments';
import type { Comment } from '../../types/comment';

interface CommentItemProps {
  id?: string;
  comment: Comment;
  currentUserId?: string;
  ascentLogId: string;
  onUpdated: (comment: Comment) => void;
  onDeleted: (commentId: string) => void;
  onReply?: (author: Comment['user']) => void;
}

export default function CommentItem({
  id,
  comment,
  currentUserId,
  ascentLogId,
  onUpdated,
  onDeleted,
  onReply,
}: CommentItemProps) {
  const { i18n } = useTranslation();
  const dateLocale = i18n.language === 'it' ? it : enUS;
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(comment.body);
  const [saving, setSaving] = useState(false);

  const isOwner = currentUserId && comment.user.id === currentUserId;

  const handleSave = async () => {
    if (!editBody.trim() || editBody === comment.body) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await commentsService.updateComment(ascentLogId, comment.id, editBody.trim());
      onUpdated(res.data);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await commentsService.deleteComment(ascentLogId, comment.id);
    onDeleted(comment.id);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditBody(comment.body);
  };

  return (
    <div id={id} className="flex gap-3 group rounded-md transition-colors duration-700">
      <Avatar className="h-8 w-8 shrink-0 mt-0.5">
        <AvatarImage src={comment.user.avatar_small_url} alt={comment.user.username} />
        <AvatarFallback className="text-xs">
          {comment.user.username.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <a
            href={`/users/${comment.user.slug}`}
            className="text-sm font-medium hover:text-primary transition-colors"
          >
            {comment.user.username}
          </a>
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: dateLocale })}
          </span>
        </div>

        {editing ? (
          <div className="mt-1 space-y-1">
            <MentionTextarea
              value={editBody}
              onChange={setEditBody}
              disabled={saving}
              minRows={2}
            />
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className="h-6 px-2" onClick={handleSave} disabled={saving}>
                <Check className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 px-2" onClick={handleCancelEdit} disabled={saving}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm mt-0.5 break-words">
            {renderCommentBody(comment.body)}
          </p>
        )}
      </div>

      {!editing && (
        <div className="flex gap-1 shrink-0">
          {onReply && !isOwner && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              onClick={() => onReply(comment.user)}
              aria-label="Reply"
            >
              <Reply className="h-3 w-3" />
            </Button>
          )}
          {isOwner && (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={() => { setEditing(true); setEditBody(comment.body); }}
              >
                <Pencil className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                onClick={handleDelete}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
